import { useState, useContext, useRef } from "react";
import {
  createEvent,
  updateEvent,
  updateReminder,
  occurrenceDeletionConfirmation,
} from "../api/EventAPI";
import AuthContext from "../context/AuthContext";
import {
  RECURRENCE_OPTIONS,
  REMINDER_OPTIONS,
  isRecurring,
  myReminder,
  myRsvpStatus,
} from "../utils/eventDomain";
import {
  DraftValidationError,
  buildEventUpdatePayload,
  initialEventSchedule,
  restartsRecurrence,
  scheduleInstants,
  seriesEditConfirmationMessage,
  setFirstDay,
  toggleAllDay,
} from "../utils/eventDrafts";
import { eventErrorMessage } from "../utils/eventErrors";
import { browserTimezone, formatDay } from "../utils/eventTime";

const datePart = (localValue) => (localValue || "").slice(0, 10);
const timePart = (localValue) => (localValue || "").slice(11, 16);

function errorMessage(err) {
  if (err instanceof DraftValidationError) return err.message;
  return eventErrorMessage(err);
}

// `event`: when provided, the form edits that event instead of creating one —
// a true one-off, a formerly recurring Event's own occurrence, or "Entire
// Series" of a recurring Event. Content always starts from the Event's
// (series) values, never an occurrence's overrides.
// `occurrence`: the occurrence the edit was opened from (optional). Its id is
// sent as occurrence_id and its times seed the schedule fields. Callers pass
// none for a rescheduled occurrence (see seriesEditOccurrence).
//
// Schedule: timed values are browser-local; all-day values are days in the
// Event's own timezone (the creator's for a new event), and the schedule is
// only sent when the user changed it — see utils/eventDrafts.
// `onSuccess`/`onClose`: optional — used when this form is hosted in a modal
// (e.g. from the Calendar page). Neither is required, so existing bare usage
// (pages/Create.js) keeps working unchanged.
export default function EventCreateForm({ event = null, occurrence = null, onSuccess, onClose }) {
  const { token } = useContext(AuthContext);
  const isEditMode = Boolean(event);

  // Recurring series (as saved) are edited whole-series: times can change
  // for every occurrence, but an occurrence can't be moved to another day —
  // the backend keeps the series' anchor date. Dates are shown read-only.
  const lockDates = isEditMode && isRecurring(event);

  // Reminders belong to the current user's RSVP and only fire for "going".
  // A new event's creator is always "going".
  const canSetReminder = !isEditMode || myRsvpStatus(event) === "going";

  // Stopping a series keeps one occurrence, so the backend requires the
  // occurrence the edit was opened from (occurrence_id).
  const canStopRepeating = !isEditMode || !isRecurring(event) || Boolean(occurrence);

  // New events are created in the creator's timezone; existing ones keep theirs.
  const [timeZone] = useState(() => (isEditMode ? event.timezone : browserTimezone()));
  const [originalSchedule] = useState(() => initialEventSchedule(event, occurrence));
  const [schedule, setSchedule] = useState(originalSchedule);
  const [form, setForm] = useState({
    title: event?.title || "",
    description: event?.description || "",
    location: event?.location || "",
    recurrence: event?.recurrence || "none",
    recurrence_ends_at: event?.recurrence_ends_at || "",
    reminder: myReminder(event),
    // Event-level (applies to the whole series). New events default to public.
    is_private: Boolean(event?.is_private),
  });
  // Opt-in when restarting recurrence: also recreate the past occurrences the
  // series missed. Sent only while offered.
  const [backfill, setBackfill] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  // Set when the backend asks to confirm removing future occurrences:
  // { payload, token, count, impact, refreshed }. `payload` is the exact PATCH body
  // that was sent; confirming resends it unchanged plus the token.
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // Synchronous guard: state updates land after the next render, so a fast
  // double click could otherwise start two confirmed saves.
  const confirmInFlight = useRef(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Time-only inputs (recurring series) edit the time half of a datetime-local value.
  const setTime = (name, time) =>
    setSchedule((sc) => ({ ...sc, [name]: `${datePart(sc[name])}T${time}` }));

  const onToggleAllDay = (e) => {
    const checked = e.target.checked;
    // Editing: switching back restores the opening schedule.
    setSchedule((sc) => toggleAllDay(sc, checked, isEditMode ? originalSchedule : null));
  };

  // Restarting a series that began in the past can restore its missed dates.
  const offersBackfill =
    isEditMode &&
    restartsRecurrence(event, form.recurrence, form.recurrence_ends_at) &&
    new Date(event.starts_at) < new Date();

  const resetForm = () => {
    setSchedule(initialEventSchedule(null));
    setForm({
      title: "",
      description: "",
      location: "",
      recurrence: "none",
      recurrence_ends_at: "",
      reminder: "none",
      is_private: false,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setOk(false);

    try {
      if (isEditMode) {
        await saveEdit(
          buildEventUpdatePayload({
            event,
            form,
            schedule,
            originalSchedule,
            occurrence,
            backfill: offersBackfill && backfill,
          })
        );
        return;
      }

      const { starts_at, ends_at } = scheduleInstants(schedule, timeZone);
      const repeats = form.recurrence !== "none";
      const eventData = await createEvent(
        {
          title: form.title,
          description: form.description,
          location: form.location,
          is_all_day: schedule.isAllDay,
          is_private: form.is_private,
          recurrence: form.recurrence,
          recurrence_ends_at: repeats && form.recurrence_ends_at ? form.recurrence_ends_at : null,
          starts_at,
          ends_at,
          reminder: form.reminder,
          // Recurrence is generated in the event's timezone, so use the
          // creator's rather than the backend default.
          timezone: timeZone,
        },
        token
      );

      setOk(true);
      onSuccess?.(eventData);
      resetForm();
      onClose?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // PATCHes an edit. If the backend asks to confirm removing future
  // occurrences, keeps `payload` as sent and shows the confirmation panel.
  const saveEdit = async (payload, confirmationToken = null) => {
    let eventData;
    try {
      eventData = await updateEvent(event.id, payload, token, confirmationToken);
    } catch (err) {
      const confirmation = occurrenceDeletionConfirmation(err);
      if (!confirmation) throw err;
      // A confirmation that comes back again means the token expired or the
      // event changed meanwhile: ask again with the refreshed token/count.
      setPendingConfirmation({ payload, ...confirmation, refreshed: Boolean(confirmationToken) });
      return;
    }

    setPendingConfirmation(null);

    // The reminder lives on the RSVP, not the Event, so it's saved
    // through the RSVP endpoint — only when it actually changed.
    if (canSetReminder && form.reminder !== myReminder(event)) {
      try {
        const rsvp = await updateReminder(event.id, form.reminder, token);
        eventData = { ...eventData, my_rsvp: rsvp };
      } catch (err) {
        console.error("Failed to update reminder after saving event", err);
        // The event itself saved: report that, keep the form open.
        onSuccess?.(eventData);
        setError(`Event saved, but the reminder couldn't be updated: ${errorMessage(err)}`);
        return;
      }
    }

    setOk(true);
    onSuccess?.(eventData);
    onClose?.();
  };

  const confirmRemoval = async () => {
    if (!pendingConfirmation || confirmInFlight.current) return;
    confirmInFlight.current = true;
    setConfirming(true);
    setError(null);
    try {
      await saveEdit(pendingConfirmation.payload, pendingConfirmation.token);
    } catch (err) {
      setPendingConfirmation(null);
      setError(errorMessage(err));
    } finally {
      confirmInFlight.current = false;
      setConfirming(false);
    }
  };

  const inputClass = "w-full border rounded px-3 py-2";
  const startDate = schedule.isAllDay ? schedule.firstDay : datePart(schedule.startsLocal);

  return (
    <div
      className="max-w-2xl mx-auto p-4 bg-white rounded-lg"
      onClick={(e) => e.stopPropagation()}
    >
      {ok && (
        <div className="mb-3 rounded bg-green-50 p-3 text-green-700">
          {isEditMode ? "Event updated!" : "Event created!"}
        </div>
      )}
      {error && <div className="mb-3 rounded bg-red-50 p-3 text-red-700">{error}</div>}

      {pendingConfirmation && (
        <div role="alertdialog" aria-labelledby="remove-occurrences-title" className="mb-4 rounded border p-4">
          <h3 id="remove-occurrences-title" className="text-base font-semibold mb-1">
            Remove upcoming occurrences?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {seriesEditConfirmationMessage(
              pendingConfirmation.impact,
              pendingConfirmation.payload.title || event.title,
              pendingConfirmation.refreshed
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={confirming}
              onClick={() => setPendingConfirmation(null)}
              className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Keep Editing
            </button>
            <button
              type="button"
              disabled={confirming}
              onClick={confirmRemoval}
              className="flex-1 rounded-full bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              {confirming ? "Saving…" : "Remove & Save"}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" hidden={Boolean(pendingConfirmation)}>
        <div>
          <label className="block text-sm mb-1">Title</label>
          <input name="title" value={form.title} onChange={onChange} className={inputClass} required />
        </div>

        <div>
          <label className="block text-sm mb-1">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={onChange}
            className={inputClass}
            rows={3}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={schedule.isAllDay} onChange={onToggleAllDay} />
          All day
        </label>

        {lockDates ? (
          // Recurring series: the date stays on the series schedule; only
          // the time can change, for every occurrence.
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {schedule.isAllDay ? (
                <div>
                  <span className="block text-sm mb-1">Date</span>
                  <span className="text-sm text-gray-700">
                    {formatDay(schedule.firstDay, { weekday: "short", month: "short", day: "numeric" })}
                  </span>
                </div>
              ) : (
                [
                  { name: "startsLocal", label: "Starts at" },
                  { name: "endsLocal", label: "Ends at" },
                ].map(({ name, label }) => (
                  <div key={name}>
                    <label htmlFor={`event-${name}`} className="block text-sm mb-1">
                      {label}
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 whitespace-nowrap">
                        {new Date(`${datePart(schedule[name])}T00:00`).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <input
                        id={`event-${name}`}
                        type="time"
                        value={timePart(schedule[name])}
                        onChange={(e) => setTime(name, e.target.value)}
                        className={inputClass}
                        required
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {canStopRepeating
                ? "Time changes apply to every event in this series."
                : "Time changes apply to every event in this series. To stop repeating, edit from one of its events in the calendar."}
            </p>
          </div>
        ) : schedule.isAllDay ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="event-first-day" className="block text-sm mb-1">
                Starts on
              </label>
              <input
                id="event-first-day"
                type="date"
                value={schedule.firstDay}
                onChange={(e) => setSchedule((sc) => setFirstDay(sc, e.target.value))}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="event-last-day" className="block text-sm mb-1">
                Ends on
              </label>
              <input
                id="event-last-day"
                type="date"
                value={schedule.lastDay}
                min={schedule.firstDay || undefined}
                onChange={(e) => setSchedule((sc) => ({ ...sc, lastDay: e.target.value }))}
                className={inputClass}
                required
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { name: "startsLocal", label: "Starts at" },
              { name: "endsLocal", label: "Ends at" },
            ].map(({ name, label }) => (
              <div key={name}>
                <label htmlFor={`event-${name}`} className="block text-sm mb-1">
                  {label}
                </label>
                <input
                  id={`event-${name}`}
                  type="datetime-local"
                  value={schedule[name]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSchedule((sc) => ({ ...sc, [name]: value }));
                  }}
                  className={inputClass}
                  required
                />
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="event-recurrence" className="block text-sm mb-1">
              Repeat
            </label>
            <select
              id="event-recurrence"
              name="recurrence"
              value={form.recurrence}
              onChange={onChange}
              className={inputClass}
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === "none" && !canStopRepeating}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {form.recurrence !== "none" && (
            <div>
              <label htmlFor="event-recurrence-ends" className="block text-sm mb-1">
                Repeat until (optional)
              </label>
              <input
                id="event-recurrence-ends"
                type="date"
                name="recurrence_ends_at"
                value={form.recurrence_ends_at}
                min={startDate || undefined}
                onChange={onChange}
                className={inputClass}
              />
            </div>
          )}
        </div>

        {offersBackfill && (
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
              Restore Past Occurrences
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Also adds the past dates this series missed while it wasn't repeating.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm mb-1">Location</label>
          <input name="location" value={form.location} onChange={onChange} className={inputClass} />
        </div>

        {canSetReminder && (
          <div>
            <label className="block text-sm mb-1">Reminder</label>
            <select name="reminder" value={form.reminder} onChange={onChange} className={inputClass}>
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span id="event-private-label" className="text-sm">
            Private Event
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={form.is_private}
            aria-labelledby="event-private-label"
            onClick={() => setForm((f) => ({ ...f, is_private: !f.is_private }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${
              form.is_private ? "bg-black" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                form.is_private ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        <div className="pt-2 space-x-1">
          <button
            disabled={submitting}
            value="published"
            className="px-4 py-2 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? (isEditMode ? "Saving…" : "Creating…") : isEditMode ? "Save" : "Create Event"}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
